/**
 * @fileoverview The recording-minutes half of the Settings usage card (X10).
 *
 * Renders one block per context (personal + one per org that seats this
 * instructor). The shape of each block is decided by `lib/usageMeter.js`, which
 * mobile shares — see that file for why the monthly allowance gets a bar and
 * the non-renewing balances deliberately do not.
 */

import UsageBar from "@/components/UsageBar";
import { buildUsageModel, formatMinutes } from "@/lib/usageMeter";

/**
 * @param {object} props
 * @param {object|null} props.usage The `/subscription/usage` payload.
 * @param {boolean} [props.labels=true] Whether each context names itself. The
 *   Settings row that hosts a single-context meter is the label, so it passes
 *   false; several contexts (an org member) keep theirs, or the bars are
 *   anonymous.
 * @returns {React.ReactElement}
 */
const RecordingUsage = ({ usage, labels = true }) => {
  const { contexts } = buildUsageModel(usage);

  return (
    <div className="flex flex-col gap-5">
      {contexts.map((ctx) => (
        <div key={ctx.key} data-testid={`usage-context-${ctx.key}`}>
          <div className={`mb-2 flex items-baseline gap-3 ${labels ? "justify-between" : "justify-end"}`}>
            {labels && <p className="text-sm text-[var(--text-secondary)]">{ctx.label}</p>}
            {ctx.showTotal && (
              <p className="font-mono text-xs text-[var(--text-primary)]" data-testid={`usage-total-${ctx.key}`}>
                {formatMinutes(ctx.totalMinutes)} left
              </p>
            )}
          </div>

          {ctx.monthly && (
            <UsageBar
              used={ctx.monthly.usedMinutes / 60}
              limit={ctx.monthly.limitMinutes / 60}
              unit="hrs"
              decimals={1}
            />
          )}

          {ctx.pools.length > 0 && (
            <div className={`flex flex-col gap-1.5 ${ctx.monthly ? "mt-3" : ""}`}>
              {ctx.pools.map((pool) => (
                <div
                  key={pool.key}
                  className="flex items-center justify-between"
                  data-testid={`usage-pool-${pool.key}`}
                >
                  <span className="text-xs text-[var(--text-secondary)]">{pool.label}</span>
                  <span className="font-mono text-xs text-[var(--text-primary)]">
                    {formatMinutes(pool.minutes)}
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* Nothing left and nothing that refills: a full bar says "used up"
              at a glance where a bare "0 min left" did not. It is the ONE bar
              drawn without a denominator, and only in the empty state, so it
              can never read as a cycle that comes back. */}
          {!ctx.monthly && ctx.pools.length === 0 && (
            <div
              className="h-1.5 w-full overflow-hidden rounded-full bg-[var(--studio-interactive)]"
              data-testid={`usage-exhausted-${ctx.key}`}
              role="img"
              aria-label="No recording minutes left"
            >
              <div className="h-full w-full rounded-full bg-[var(--state-danger)]" />
            </div>
          )}

          <p className="mt-2 font-sans text-[12px] text-[var(--text-tertiary)]">{ctx.note}</p>
          {ctx.accountingNote && (
            <p className="mt-2 font-sans text-xs text-[var(--text-secondary)]">{ctx.accountingNote}</p>
          )}
        </div>
      ))}
    </div>
  );
};

export default RecordingUsage;
